import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DistrictSchemas, type UpdateDistrict } from "@workspace/contracts/districts";
import { LanguageValues } from "@workspace/contracts/language";
import { Separator } from "@workspace/ui/components/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/ui/components/tabs";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { useAppForm } from "@/components/form-inputs";
import { AutoFillAltText } from "@/components/form-inputs/auto-fill-alt-text";
import { SlugField } from "@/features/common/slug-field";
import { DistrictSectionGroupField } from "@/features/district/district-sections-group";
import { Main } from "@/layout/main";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/districts/$districtId/update")({
  component: RouteComponent,
  loader: async ({ params, context: { orpc, queryClient } }) => {
    return queryClient.ensureQueryData(orpc.districts.findById.queryOptions({ input: { id: params.districtId } }));
  },
});

function RouteComponent() {
  const { districtId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: district } = useSuspenseQuery(orpc.districts.findById.queryOptions({ input: { id: districtId } }));

  const citiesList = useQuery(orpc.cities.options.queryOptions({ input: {} }));

  const { mutateAsync } = useMutation(orpc.districts.update.mutationOptions());

  const form = useAppForm({
    defaultValues: {
      id: district.id,
      cityId: district.cityId,
      slug: district.slug,
      image: district.image ?? undefined,
      altText: district.altText,
      translations: district.translations,
      sections: district.districtSections,
    } as UpdateDistrict,

    validators: {
      onSubmit: DistrictSchemas.update,
    },

    onSubmit: async ({ value }) => {
      try {
        await mutateAsync({ ...value, altText: value.altText ?? undefined });
        toast.success("District has been updated successfully");

        queryClient.invalidateQueries({ queryKey: orpc.districts.list.queryKey({ input: {} }) });

        navigate({ to: "/districts" });
      } catch (error) {
        const msg = getError(error, "Failed to update district");
        toast.error(msg);
      }
    },
  });

  const onSubmitHandler = (e: React.SubmitEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <Main>
      <form onSubmit={onSubmitHandler}>
        <Tabs defaultValue="district" className="relative w-full">
          <TabsList>
            <TabsTrigger value="district">District</TabsTrigger>
            <TabsTrigger value="sections">Sections</TabsTrigger>
          </TabsList>

          <TabsContent value="district" className="grid grid-cols-2 gap-4 pt-5">
            <form.Subscribe selector={(state) => ({ title: state.values.translations.en.title, altText: state.values.altText ?? "" })}>
              {({ title, altText }) => <AutoFillAltText title={title} altText={altText} setAltText={(value) => form.setFieldValue("altText", value)} />}
            </form.Subscribe>

            {/*<TranslationGroupField
              label="Name"
              type="text"
              form={form}
              fields={{
                en: "translations.en.title",
                ar: "translations.ar.title",
                tr: "translations.tr.title",
                fa: "translations.fa.title",
              }}
            />*/}

            <form.AppField name="translations.en.title">
              {(field) => <field.TextField label="Name English" />}
            </form.AppField>

            <form.AppField name="translations.ar.title">
              {(field) => <field.TextField label="Name Arabic" />}
            </form.AppField>

            <form.AppField name="translations.fa.title">
              {(field) => <field.TextField label="Name Farsi" />}
            </form.AppField>

            <form.AppField name="translations.tr.title">
              {(field) => <field.TextField label="Name Turkish" />}
            </form.AppField>

            <Separator className="col-span-2 my-4" />

            <Tabs defaultValue="en" className="col-span-2">
              <TabsList>
                <TabsTrigger value="en">English</TabsTrigger>
                <TabsTrigger value="ar">Arabic</TabsTrigger>
                <TabsTrigger value="tr">Turkish</TabsTrigger>
                <TabsTrigger value="fa">Farsi</TabsTrigger>
              </TabsList>

              {LanguageValues.map((lang) => (
                <TabsContent key={lang} value={lang}>
                  <form.AppField name={`translations.${lang}.description`}>
                    {(field) => (
                      <field.EditorField
                        direction={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}
                        preview
                        label={`${lang.toUpperCase()} Description`}
                      />
                    )}
                  </form.AppField>
                </TabsContent>
              ))}
            </Tabs>

            <Separator className="col-span-2 my-4" />

            <form.Subscribe selector={(state) => state.values.translations.en.title}>
              {(name) => (
                <form.AppField name="slug">
                  {(field) => (
                    <SlugField
                      name={name}
                      onBlur={field.handleBlur}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.AppField>
              )}
            </form.Subscribe>

            <form.AppField name="cityId">
              {(field) => (
                <field.SelectField options={citiesList.data || []} label="City" placeholder="Select district" />
              )}
            </form.AppField>

            <Separator className="col-span-2 my-4" />

            <form.AppField name="image">
              {(field) => <field.ImageField label="Image" classNames={{ wrapper: "col-span-2" }} />}
            </form.AppField>

            <form.Subscribe selector={(state) => state.values.image}>
              {(image) => (
                <form.AppField name="altText">
                  {(field) => (
                    <field.TextField disabled={!image} label="Alt Text" classNames={{ wrapper: "col-span-2" }} />
                  )}
                </form.AppField>
              )}
            </form.Subscribe>
          </TabsContent>

          <TabsContent value="sections">
            <DistrictSectionGroupField form={form} fields={{ sections: "sections" }} />
          </TabsContent>
        </Tabs>

        <form.AppForm>
          <form.SubmitButton className="float-end mt-8">Submit</form.SubmitButton>
        </form.AppForm>
      </form>
    </Main>
  );
}
